/* eslint-disable no-console */
import {
  Image,
  Link,
  ListBullets,
  ListNumbers,
  TextAlignCenter,
  TextAlignLeft,
  TextAlignRight,
  TextB,
  TextItalic,
  TextStrikethrough,
  TextUnderline,
} from '@phosphor-icons/react'
import { Editor } from '@tiptap/react'
import { Input } from 'antd'
import { ReactNode, useCallback, useMemo } from 'react'

import Tooltip from '@/ui/atoms/Tooltip'
import Modal from '@/ui/Modal'
import cn from '@/utils/tailwind/cn'

import { FileHandlerExtension } from '../extensions/fileHandler'
import { useLinkEditor } from '../hooks/useLinkEditor'
import { TextEditorToolbarHeading } from './TextEditorToolbarHeading'
import { TextEditorToolbarHighlight } from './TextEditorToolbarHighlight'
import { TextEditorToolbarTable } from './TextEditorToolbarTable'
import { TextEditorToolbarTextColor } from './TextEditorToolbarTextColor'
import { TextEditorToolbarVariables } from './TextEditorToolbarVariables'
import { TooltipLink } from './TooltipLink'

type ToolbarItem = {
  id: string
  onClick?: () => void
  icon: ReactNode
  description: string
}

type ToolbarGroup = {
  id: string
  tools: ToolbarItem[]
}

type Props = {
  isToolbarTopActive: boolean
  getContainer?: () => HTMLElement
  editor: Editor
  tools?: (
    | 'bold'
    | 'italic'
    | 'strike'
    | 'underline'
    | 'variables'
    | 'bulletList'
    | 'orderedList'
    | 'link'
    | 'image'
    | 'left'
    | 'center'
    | 'right'
    | 'table'
    | 'size'
    | 'color'
    | 'highlight'
  )[]
}

const Divider = () => <div className="h-6 w-[1px] bg-gray-200" />

export const Toolbar = ({
  getContainer,
  editor,
  isToolbarTopActive,
  tools = ['bold', 'italic', 'strike', 'underline', 'variables'],
}: Props) => {
  const {
    onSubmit,
    handleCloseModal,
    openAddLinkModal,
    openEditLinkModal,
    setLinkText,
    setUrl,
    isModalOpen,
    linkText,
    url,
  } = useLinkEditor({ editor })

  const handleImageUpload = useCallback(() => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/png, image/jpeg, image/gif, image/webp'
    input.onchange = async (event: Event) => {
      const file = (event.target as HTMLInputElement).files?.[0]
      if (!file) return

      try {
        if (editor && editor.commands) {
          const fileHandlerExtension = editor.extensionManager.extensions?.find(
            (ext) => ext.name === 'fileHandler'
          ) as FileHandlerExtension

          if (fileHandlerExtension?.options?.onUpload) {
            const src = await fileHandlerExtension.options.onUpload(file)
            if (src) {
              editor.chain().focus().insertImage({ src }).run()
            }
          } else {
            const reader = new FileReader()
            reader.onload = () => {
              editor
                .chain()
                .focus()
                .insertImage({ src: reader.result as string })
                .run()
            }
            reader.readAsDataURL(file)
          }
        }
      } catch (error) {
        console.error('Erro ao fazer upload da imagem:', error)
      }
    }
    input.click()
  }, [editor])

  const toolbarList: ToolbarGroup[] = useMemo(
    () => [
      {
        id: 'formatting',
        tools: [
          {
            id: 'bold',
            onClick: () => editor.chain().focus().toggleBold().run(),
            icon: <TextB weight="bold" />,
            description: 'Bold',
          },
          {
            id: 'italic',
            onClick: () => editor.chain().focus().toggleItalic().run(),
            icon: <TextItalic weight="bold" />,
            description: 'Italic',
          },
          {
            id: 'underline',
            onClick: () => editor.chain().focus().toggleUnderline().run(),
            icon: <TextUnderline weight="bold" />,
            description: 'Underline',
          },
          {
            id: 'strike',
            onClick: () => editor.chain().focus().toggleStrike().run(),
            icon: <TextStrikethrough weight="bold" />,
            description: 'Strike',
          },
        ],
      },
      {
        id: 'lists',
        tools: [
          {
            id: 'link',
            onClick: openAddLinkModal,
            icon: <Link weight="bold" />,
            description: 'Add Link',
          },
          {
            id: 'bulletList',
            onClick: () => editor.chain().focus().toggleBulletList().run(),
            icon: <ListBullets weight="bold" />,
            description: 'Bullet List',
          },
          {
            id: 'orderedList',
            onClick: () => editor.chain().focus().toggleOrderedList().run(),
            icon: <ListNumbers weight="bold" />,
            description: 'Ordered List',
          },
        ],
      },
      {
        id: 'text',
        tools: [
          {
            id: 'size',
            icon: <TextEditorToolbarHeading editor={editor} />,
            description: 'Text size',
          },
          {
            id: 'color',
            icon: <TextEditorToolbarTextColor editor={editor} />,
            description: 'Text color',
          },
          {
            id: 'highlight',
            onClick: undefined,
            icon: <TextEditorToolbarHighlight editor={editor} />,
            description: 'Highlight color',
          },
        ],
      },
      {
        id: 'others',
        tools: [
          {
            id: 'image',
            onClick: handleImageUpload,
            icon: <Image weight="bold" />,
            description: 'Add Image',
          },
          {
            id: 'table',
            icon: <TextEditorToolbarTable editor={editor} />,
            description: 'Table',
          },
        ],
      },
      {
        id: 'aligns',
        tools: [
          {
            id: 'left',
            onClick: () => editor.chain().focus().setTextAlign('left').run(),
            icon: <TextAlignLeft weight="bold" />,
            description: 'Align left',
          },
          {
            id: 'center',
            onClick: () => editor.chain().focus().setTextAlign('center').run(),
            icon: <TextAlignCenter weight="bold" />,
            description: 'Align center',
          },
          {
            id: 'right',
            onClick: () => editor.chain().focus().setTextAlign('right').run(),
            icon: <TextAlignRight weight="bold" />,
            description: 'Align right',
          },
        ],
      },
      {
        id: 'variables',
        tools: [
          {
            id: 'variables',
            icon: <TextEditorToolbarVariables editor={editor} />,
            description: 'Insert Variable',
          },
        ],
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editor]
  )

  const filteredToolbarList = useMemo(() => {
    const activeGroups = toolbarList
      .map((group) => ({
        ...group,
        tools: group?.tools?.filter((tool) =>
          tools.includes(tool?.id as keyof Props['tools'])
        ),
      }))
      .filter((group) => group.tools?.length)

    return activeGroups.flatMap((group, index) => {
      const elements = group?.tools?.map((tool) => tool)

      if (index) {
        return [<Divider key={`divider-${group.id}`} />, ...(elements || [])]
      }
      return elements
    })
  }, [toolbarList, tools])

  return (
    <>
      <div
        className={cn(
          'absolute left-0 top-0 flex h-fit w-full items-center gap-2.5 border-b !border-gray-200 px-2.5 py-1.5 transition-all duration-150',
          isToolbarTopActive ? 'opacity-100' : 'invisible opacity-0'
        )}
      >
        {filteredToolbarList.map((item) => {
          if (typeof item === 'object' && 'id' in item) {
            const isActive = editor.isActive(item.id)
            return (
              <Tooltip key={item.id} title={item.description}>
                <div>
                  {item?.onClick ? (
                    <button
                      className={cn(
                        'flex h-6 items-center justify-center rounded-sm border-2 border-transparent bg-transparent p-0 text-xl text-gray-700 outline-none hover:text-primary focus:outline-none focus-visible:outline-none disabled:cursor-not-allowed',
                        isActive && 'text-primary'
                      )}
                      disabled={!editor.isEditable}
                      onClick={item.onClick}
                      type="button"
                    >
                      {item.icon}
                    </button>
                  ) : (
                    item.icon
                  )}
                </div>
              </Tooltip>
            )
          }
          return item
        })}
      </div>
      <Modal
        centered
        closable
        destroyOnClose
        cancelText="Cancel"
        className="[&_.ant-modal-content]:rounded-[4px] [&_.ant-modal-header]:rounded-[4px]"
        getContainer={getContainer}
        okText="Ok"
        onCancel={handleCloseModal}
        onOk={onSubmit}
        open={isModalOpen}
        title="Add Link"
      >
        <div className="flex flex-col gap-4">
          <div>
            <h4>Text</h4>
            <Input
              autoFocus={!linkText}
              onChange={(e) => setLinkText(e.target.value)}
              value={linkText as string}
            />
          </div>

          <div>
            <h4>Link</h4>

            <Input
              autoFocus={!!linkText}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Paste or type the link"
              value={url as string}
            />
          </div>
        </div>
      </Modal>
      <TooltipLink editor={editor} handleEdit={openEditLinkModal} />
    </>
  )
}
